import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { LogIn, Loader, Shield, Database } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('superadmin@coolprofessors.com');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setLoading(true);
    setError('');

    console.log('🔄 Submitting login form...');
    
    try {
      const result = await login(email, password);
      
      if (!result.success) {
        setError(result.message);
        console.error('❌ Login failed:', result.message);
      } else {
        console.log('✅ Login successful!');
        
        // Store admin data in localStorage for Admin Management component
        if (result.admin) {
          const adminData = {
            id: result.admin.id,
            email: result.admin.email,
            role: result.admin.role,
            name: result.admin.name || result.admin.email.split('@')[0],
            permissions: result.admin.permissions || [],
            last_login: new Date().toISOString()
          };
          
          // Store in both localStorage and sessionStorage for redundancy
          localStorage.setItem('currentAdmin', JSON.stringify(adminData));
          sessionStorage.setItem('currentAdmin', JSON.stringify(adminData));
          
          console.log('💾 Admin data stored:', {
            email: adminData.email,
            role: adminData.role,
            id: adminData.id
          });
        }
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      setError('An unexpected error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  // Test backend connection with correct endpoint
  const testBackendConnection = async () => {
    try {
      console.log('🔌 Testing backend connection...');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/health`);
      const data = await response.json();
      console.log('✅ Backend connection test:', data);
      alert(`✅ Backend Connection Successful!\n\nStatus: ${data.status}\nMessage: ${data.message}\nEnvironment: ${data.environment || 'Unknown'}`);
    } catch (error) {
      console.error('❌ Backend connection failed:', error);
      
      // Try alternative endpoints
      try {
        console.log('🔄 Trying alternative endpoint...');
        const altResponse = await fetch(process.env.REACT_APP_API_URL.replace('/api', ''));
        const altText = await altResponse.text();
        console.log('⚠️ Alternative endpoint response:', altText);
        alert(`⚠️ Partial Backend Connection\n\nMain API endpoint failed but server is running.\nCheck if /api/health endpoint exists.`);
      } catch (altError) {
        console.error('❌ All connection attempts failed:', altError);
        alert('❌ Backend Connection FAILED\n\nPlease ensure:\n• Backend server is running on port 5000\n• CORS is properly configured\n• No firewall blocking the connection');
      }
    }
  };

  // Quick test credentials fill
  const fillTestCredentials = (role) => {
    const credentials = {
      super_admin: {
        email: 'superadmin@coolprofessors.com',
        password: 'password123'
      },
      minister_admin: {
        email: 'minister@coolprofessors.com', 
        password: 'password123'
      },
      support_admin: {
        email: 'support@coolprofessors.com',
        password: 'password123'
      }
    };
    
    const creds = credentials[role];
    if (creds) {
      setEmail(creds.email);
      setPassword(creds.password);
      setError('');
      console.log(`🔑 Filled ${role} credentials`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 rounded-2xl shadow-lg">
            <Shield className="h-10 w-10 text-white" />
          </div>
        </div>
        <h2 className="mt-6 text-center text-4xl font-bold tracking-tight text-gray-900">
          CoolProfessors
        </h2>
        <p className="mt-2 text-center text-lg text-gray-600 font-medium">
          Admin Dashboard
        </p>
        <p className="mt-1 text-center text-sm text-gray-500">
          Secure access to system management
        </p>
        
        {/* Backend Test Button */}
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={testBackendConnection}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
          >
            <Database className="h-4 w-4 mr-2" />
            Test Backend Connection
          </button>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-6 shadow-xl sm:rounded-2xl sm:px-10 border border-gray-200">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm font-medium">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Shield className="h-4 w-4 text-red-500" />
                  </div>
                  <div className="ml-3">
                    <p>{error}</p>
                  </div>
                </div>
              </div>
            )}
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full appearance-none rounded-xl border border-gray-300 px-4 py-3 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                  placeholder="admin@coolprofessors.com"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full appearance-none rounded-xl border border-gray-300 px-4 py-3 placeholder-gray-400 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            {/* Quick Test Credentials */}
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
              <p className="text-xs font-medium text-gray-600 mb-2">Quick Test (Fill Credentials):</p>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => fillTestCredentials('super_admin')}
                  className="flex-1 px-3 py-2 bg-red-100 text-red-700 text-xs font-medium rounded-lg hover:bg-red-200 transition-colors duration-200"
                >
                  Super Admin
                </button>
                <button
                  type="button"
                  onClick={() => fillTestCredentials('minister_admin')}
                  className="flex-1 px-3 py-2 bg-blue-100 text-blue-700 text-xs font-medium rounded-lg hover:bg-blue-200 transition-colors duration-200"
                >
                  Minister Admin
                </button>
                <button
                  type="button"
                  onClick={() => fillTestCredentials('support_admin')}
                  className="flex-1 px-3 py-2 bg-green-100 text-green-700 text-xs font-medium rounded-lg hover:bg-green-200 transition-colors duration-200"
                >
                  Support Admin
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="flex w-full justify-center items-center rounded-xl border border-transparent bg-gradient-to-r from-blue-600 to-indigo-600 py-3 px-4 text-base font-medium text-white shadow-lg hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:-translate-y-0.5"
              >
                {loading ? (
                  <>
                    <Loader className="h-5 w-5 animate-spin mr-3" />
                    Signing in...
                  </>
                ) : (
                  <>
                    <LogIn className="h-5 w-5 mr-3" />
                    Sign in to Dashboard
                  </>
                )}
              </button>
            </div>
          </form>
          
          {/* System Info */}
          <div className="mt-8 p-4 bg-blue-50 rounded-xl border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-800">System Information</p>
                <p className="text-xs text-blue-600 mt-1">
  Backend: {process.env.REACT_APP_API_URL || 'http://localhost:5000'}
</p>
              </div>
              <Database className="h-4 w-4 text-blue-500" />
            </div>
            <div className="mt-2 text-xs text-blue-600">
              <p>• Ensure backend is running on port 5000</p>
              <p>• Check browser console for detailed logs</p>
            </div>
          </div>

          {/* Security Notice */}
          <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-xs text-amber-700 text-center">
              🔒 This is a secure admin system. Unauthorized access is prohibited.
            </p>
          </div>
        </div>
      </div>

      {/* Version Info */}
      <div className="mt-8 text-center">
        <p className="text-xs text-gray-500">
          CoolProfessors Admin Dashboard v1.0 • Secure Access System
        </p>
      </div>
    </div>
  );
};

export default Login;